import { post } from '@/api/helpers';
import {
  ConsentRequiredResType,
  LoginSSOResponse,
  LoginUserResType,
} from '@/api/types';
import { TOS_CONSENT_REQUIRED } from '@/app/shared/constants';
import { toastTryAgain } from '@/app/shared/helpers';
import { publishReceiveLoginResult } from '@/app/shared/publishEventHelpers';
import { WhiteLabelLoginParams } from '@/app/types/types';
import { logErrorInfo } from '@/RollbarErrorTracking';

export const loginUser = async (payload: {
  userName: string;
  password: string;
  acceptTos?: boolean;
  whiteLabelBusiness?: WhiteLabelLoginParams;
}) => {
  const curPayload = {
    ...payload,
    cookieConsentDocRef: 'Cookies-2018-05-25',
  };
  const response = await post({
    endpoint: '/webapp/login',
    payload: curPayload,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);

    if (
      response.status === 403 &&
      errorBody?.message === TOS_CONSENT_REQUIRED
    ) {
      return { consentRequired: true } as ConsentRequiredResType;
    }

    logErrorInfo(errorBody ?? { message: response.statusText });

    if (response.status === 400) {
      return { ok: false };
    } else {
      toastTryAgain('Login failed. Please refresh the page and try again.');
      publishReceiveLoginResult({
        resultType: 'ERROR',
        errorMessage: errorBody?.message,
        whiteLabel: payload.whiteLabelBusiness && payload.whiteLabelBusiness,
      });
      throw new Error('Login failed');
    }
  }

  return response.json() as Promise<LoginUserResType>;
};

export const loginWithSSO = async (payload: {
  email: string;
  whiteLabelBusiness: WhiteLabelLoginParams;
  cookieConsentDocRef: string;
}) => {
  const response = await post({
    endpoint: '/espresso/v1/login/sso',
    payload,
  });

  if (!response.ok) {
    logErrorInfo({ message: response.statusText });
    toastTryAgain('Error occurred. Please refresh the page and try again.');
    throw new Error('SSO login failed');
  }

  try {
    const responseObject: LoginSSOResponse = await response.json();
    const { redirectUrl } = responseObject;
    if (!redirectUrl) throw new Error('SSO login failed');

    const apiDomain = process.env.NEXT_PUBLIC_API_DOMAIN;
    const ssoUrl = `${apiDomain}/espresso${redirectUrl}`;
    return { ssoUrl };
  } catch {
    toastTryAgain('Error occurred. Please refresh the page and try again.');
    throw new Error('SSO login failed');
  }
};
